import { useCallback, useEffect, useMemo, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../../store/app/hooks"
import {
  setOpenAlert,
  setLoadingDelete,
  setCloseAlert,
} from "../../../store/features/alert/alertSlice"
import { useLazyQuery, useMutation, useQuery } from "@apollo/client"
import {
  selectOrderPagination,
  selectOrderQuery,
  setOrderPagination,
  setOrderQuery,
} from "../../../store/features/order/orderSlice"
import { graphql } from "../../../gql"
import CustomTable from "../../Common/CustomTable/CustomTable"
import Loader from "../../Common/Loader/Loader"
import { getErrorsAsString } from "../../../utils/getErrorsAsString"
import {
  Action,
  Order,
  OrderWhereInput,
  SortOrder,
  Status,
} from "../../../gql/graphql"
import { MRT_ColumnDef } from "material-react-table"
import { Pagination } from "../../../types/Pagination"
import { setOpenSnackbar } from "../../../store/features/snackbar/snackbarSlice"
import { updateCacheOrder } from "../../../caches/updateCacheOrder"
import { useNavigate } from "react-router-dom"
import { setTitle } from "../../../store/features/menu/menuSlice"
import moment from "moment"
import useOrder from "../../../hooks/useOrder"
import { havePermissions, whereInputClause } from "../../../utils/permissions"
import OrderImages from "./OrderImages/OrderImages"
import { getUserCountryId } from "../../../utils/authToken"
import { Rating, ToggleButton, ToggleButtonGroup } from "@mui/material"
import useLang from "../../../hooks/useLang"
import ModalDetailCraftman from "../../Craftman/ModalDetailCraftman/ModalDetailCraftman"
import ModalDetailUser from "../../User/ModalDetailUser/ModalDetailUser"
import ModalDetailOrder from "../ModalDetailOrder/ModalDetailOrder"
import { usePersistedFilters } from "../../../hooks/usePersistedFilters"

// ─── Storage key constant ─────────────────────────────────────────────────────
const ORDER_FILTERS_KEY = "order_list_filters"

// ─── GraphQL queries ──────────────────────────────────────────────────────────
export const LIST_ORDERS = graphql(`
  query OrdersList(
    $where: OrderWhereInput
    $orderBy: [OrderOrderByWithRelationAndSearchRelevanceInput!]
    $take: Int
    $skip: Int
    $pricesOrderBy2: [OrderPriceOrderByWithRelationAndSearchRelevanceInput!]
    $aggregateOrderWhere2: OrderWhereInput
  ) {
    orders(where: $where, orderBy: $orderBy, take: $take, skip: $skip) {
      id
      oldID
      deadline
      status
      createdAt
      updatedAt
      delivryDate
      NoticeOrders {
        id
        description
        note
      }
      DeliveryAdress {
        address
        cityName
        zipCode
        Country {
          name_fr
        }
      }
      orderStatus {
        haveAmountNotPaid
        haveAmountPaid
        notPaid
      }
      orderInvoice {
        matiere
        materiale
        quantity
        craftsmanAmount
        shippingCost
        serviceCharge
        orderFee
        currency
        paimenproPrice
      }
      Customer {
        id
        email
        name
        lastName
        phoneNumber
        country {
          currency
        }
      }
      Craftman {
        id
        email
        name
        lastName
        phoneNumber
        country {
          id
          name_fr
          name_en
        }
      }
      Country {
        id
        name_fr
        currency
      }
      Categories {
        id
        name_fr
      }
      ItemTypes {
        id
        name_fr
      }
      MaterialTypes {
        id
        name_fr
      }
      Photos {
        id
        name
        url
      }
      Prices(orderBy: $pricesOrderBy2) {
        price
      }
    }
    aggregateOrder(where: $aggregateOrderWhere2) {
      _count {
        _all
      }
    }
  }
`)

export const DELETE_ORDER = graphql(`
  mutation DeleteOneOrder($where: OrderWhereUniqueInput!) {
    deleteOneOrder(where: $where) {
      id
    }
  }
`)

const LIST_FILTER = graphql(`
  query filtersListOrder($where: CountryWhereInput) {
    categories {
      id
      name_fr
      name_en
    }
    itemTypes {
      name_fr
      name_en
      id
    }
    materialTypes {
      name_fr
      name_en
      id
    }
    countries(where: $where) {
      name_fr
      name_en
      id
    }
  }
`)

const ARCHIVE_MANY = graphql(`
  mutation UpdateManyOrder(
    $data: OrderUpdateManyMutationInput!
    $where: OrderWhereInput
  ) {
    updateManyOrder(data: $data, where: $where) {
      count
    }
  }
`)

// ─── Props ────────────────────────────────────────────────────────────────────
interface Props {
  extraQuery?: OrderWhereInput
  isOnlyCraftman?: boolean
  isOnlyCustomer?: boolean
}

// ─── Component ────────────────────────────────────────────────────────────────
const ListOrder = ({ extraQuery, isOnlyCraftman, isOnlyCustomer }: Props) => {
  const { getText, getByLang } = useLang()
  const { page, size } = useAppSelector(selectOrderPagination)
  const query = useAppSelector(selectOrderQuery)
  // eslint-disable-next-line no-restricted-globals
  const href = location.href
  const url = new URL(href)
  const countryId = getUserCountryId()
  const status = url.searchParams.get("status")
  const dispatch = useAppDispatch()
  const { getCustomerOrderPrice } = useOrder()
  const navigate = useNavigate()

  // ── Persisted filters via reusable hook ───────────────────────────────────
  const { columnFilters, setColumnFilters } =
    usePersistedFilters(ORDER_FILTERS_KEY)

  // ── Local UI state ────────────────────────────────────────────────────────
  const [filterMobile, setFilterMobile] = useState("")
  const [ids, setIds] = useState<number[]>([])
  const [craftmanId, setCraftmanId] = useState(0)
  const [openDetailCraftman, setOpenDetailCraftman] = useState(false)
  const [userId, setUserId] = useState(0)
  const [openDetailUser, setOpenDetailUser] = useState(false)
  const [orderId, setOrderId] = useState(0)
  const [openDetailOrder, setOpenDetailOrder] = useState(false)

  // ── Filter data query ─────────────────────────────────────────────────────
  const { loading: loadingFilter, data: dataFilter } = useQuery(LIST_FILTER, {
    variables: {
      where: { isOpen: { equals: true } },
    },
  })

  const [archiveMany] = useMutation(ARCHIVE_MANY)

  // ── Filter helpers ────────────────────────────────────────────────────────
  const getValueFilter = useCallback(
    (el: { name_fr: string; name_en: string; id: number }) => ({
      label: getByLang({ value_fr: el.name_fr, value_en: el.name_en }),
      value: el.id.toString(),
      text: getByLang({ value_fr: el.name_fr, value_en: el.name_en }),
    }),
    [getByLang],
  )

  const handleChangeFilterMobile = (
    _event: React.MouseEvent<HTMLElement>,
    newFilter: string,
  ) => {
    setFilterMobile(newFilter)
  }

  const handleSelected = (id: number) => {
    setIds((prev) =>
      prev.includes(id) ? prev.filter((el) => el !== id) : [...prev, id],
    )
  }

  const filterStatus = useMemo(
    () => [
      { label: getText("Payé"), value: "paid", text: getText("Payé") },
      {
        label: getText("En cours"),
        value: "inProgress",
        text: getText("En cours"),
      },
      {
        label: getText("Archivée"),
        value: "archived",
        text: getText("Archivée"),
      },
    ],
    [getText],
  )

  const filterQuestion = useMemo(
    () => [
      {
        label: getText("Sélectionner tous"),
        value: "all",
        text: getText("Sélectionner tous"),
      },
      { label: getText("OUI"), value: "yes", text: getText("OUI") },
      { label: getText("NON"), value: "no", text: getText("NON") },
    ],
    [getText],
  )

  const getFilters: () => OrderWhereInput = useCallback(() => {
    if (!columnFilters.length) return {}

    return columnFilters.reduce(
      (
        accumulator: OrderWhereInput,
        { id, value }: { id: string; value: any },
      ) => {
        switch (id) {
          case "NoticeOrders":
            if (value !== "all") {
              accumulator = {
                ...accumulator,
                NoticeOrders: value === "yes" ? { some: {} } : { none: {} },
              }
            }
            break

          case "Pays de l'artisan":
            accumulator = {
              ...accumulator,
              Craftman: {
                is: { countryId: { equals: Number(value) } },
              },
            }
            break

          case "Statut":
          case "Paiment":
            accumulator = {
              ...accumulator,
              ...(value === "paid"
                ? {
                    OR: [
                      { status: { equals: Status.Paid } },
                      {
                        ActionOrders: {
                          some: { action: { equals: Action.Paid } },
                        },
                      },
                      { status: { equals: Status.Delivry } },
                    ],
                  }
                : value === "askDeliveryMan"
                ? { AskDeliveryMan: { some: {} } }
                : {
                    status: {
                      in:
                        value === "archived"
                          ? [Status.Archive]
                          : [Status.Pending],
                    },
                  }),
            }
            break

          case "Date de livraison":
            accumulator = {
              ...accumulator,
              delivryDate: { gte: value.toDate() },
            }
            break

          case "Date de commande":
            accumulator = {
              ...accumulator,
              createdAt: { gte: value.toDate() },
            }
            break

          case "id":
            accumulator = {
              ...accumulator,
              id: { equals: Number(value) },
            }
            break

          case "oldID":
            accumulator = {
              ...accumulator,
              oldID: { equals: Number(value) },
            }
            break

          case "Prix du client":
            accumulator = {
              ...accumulator,
              Prices: { some: { price: { equals: Number(value) } } },
            }
            break

          case "Devise":
            accumulator = {
              ...accumulator,
              Customer: {
                is: {
                  country: { is: { currency: { search: value } } },
                },
              },
            }
            break

          case "Catégorie":
            accumulator = {
              ...accumulator,
              Categories: { some: { id: { equals: Number(value) } } },
            }
            break

          case "Type article":
            accumulator = {
              ...accumulator,
              ItemTypes: { some: { id: { equals: Number(value) } } },
            }
            break

          case "Type matière":
            accumulator = {
              ...accumulator,
              MaterialTypes: { some: { id: { equals: Number(value) } } },
            }
            break

          case "ID du client":
            accumulator = {
              ...accumulator,
              customerId: { equals: Number(value) },
            }
            break

          case "ID de l'artisan":
            accumulator = {
              ...accumulator,
              craftmanId: { equals: Number(value) },
            }
            break
        }
        return accumulator
      },
      {},
    )
  }, [columnFilters])

  const queryName = useCallback(() => {
    if (!query) return {}
    return {
      OR: [
        { Customer: { is: { name: { search: query } } } },
        { Customer: { is: { lastName: { search: query } } } },
        { Craftman: { is: { name: { search: query } } } },
        { Craftman: { is: { lastName: { search: query } } } },
        { id: { equals: Number(query) } },
        { oldID: { equals: Number(query) } },
      ],
    }
  }, [query])

  const whereCluase = whereInputClause({
    country: { is: { id: { equals: countryId } } },
  })

  // ── Main data query ───────────────────────────────────────────────────────
  const [getOrders, { loading, data, refetch }] = useLazyQuery(LIST_ORDERS)

  useEffect(() => {
    getOrders({
      variables: {
        take: size,
        skip: page * size,
        orderBy: [
          { updatedAt: SortOrder.Desc },
          ...(filterMobile === "order_current"
            ? [{ createdAt: SortOrder.Desc }]
            : [
                { id: SortOrder.Desc },
                { NoticeOrders: { _count: SortOrder.Desc } },
              ]),
        ],
        pricesOrderBy2: { createdAt: SortOrder.Desc },
        where: {
          ...(status === "all_paid"
            ? { ActionOrders: { some: { action: { equals: Action.Paid } } } }
            : {
                status: {
                  in:
                    status === "paid" || filterMobile === "order_paid"
                      ? [Status.Paid, Status.Delivry]
                      : status === "archive"
                      ? [Status.Archive]
                      : status === "pending"
                      ? [Status.Paid, Status.Pending]
                      : [
                          Status.Paid,
                          Status.Delivry,
                          Status.Pending,
                          Status.Archive,
                        ],
                },
              }),
          Craftman: { is: { ...whereCluase } },
          ...(filterMobile === "order_have_price" && { Prices: { some: {} } }),
          ...queryName(),
          ...getFilters(),
          ...extraQuery,
        },
        aggregateOrderWhere2: {
          Craftman: { is: { ...whereCluase } },
          status: {
            in:
              status === "paid" || filterMobile === "order_paid"
                ? [Status.Paid, Status.Delivry]
                : [Status.Paid, Status.Delivry, Status.Pending, Status.Archive],
          },
          ...queryName(),
          ...getFilters(),
          ...extraQuery,
        },
      },
    })
  }, [
    getOrders,
    page,
    query,
    queryName,
    size,
    status,
    getFilters,
    filterMobile,
  ])

  // ── Mutations ─────────────────────────────────────────────────────────────
  const [deleteOrder] = useMutation(DELETE_ORDER)

  // ── Columns ───────────────────────────────────────────────────────────────
  const style = { backgroundColor: "black", padding: 5 }

  const columns = useMemo<MRT_ColumnDef<Order>[]>(
    () => [
      {
        accessorFn: (row) => (
          <div>
            <OrderImages images={row.Photos} />
          </div>
        ),
        header: getText("Photos commande"),
        enableClickToCopy: true,
        enableColumnFilter: false,
      },
      {
        accessorFn: (row) => (
          <span>
            {row.status === Status.Paid
              ? getText("Payé")
              : row.status === Status.Archive
              ? getText("Archivée")
              : getText("En cours")}
          </span>
        ),
        header: getText("Statut"),
        enableClickToCopy: true,
        filterVariant: "select",
        filterSelectOptions: [
          ...filterStatus,
          {
            label: getText("Demande de livreur"),
            value: "askDeliveryMan",
            text: getText("Demande de livreur"),
          },
        ],
        id: "Statut",
      },
      {
        accessorFn: (row) =>
          row.delivryDate
            ? moment(row.delivryDate).format("DD MMM YYYY [à] HH[h]mm")
            : getText("Pas definie"),
        header: getText("Date de livraison"),
        enableClickToCopy: true,
        filterVariant: "date",
        id: "Date de livraison",
      },
      {
        header: getText("NoticeOrders"),
        id: "NoticeOrders",
        filterVariant: "select",
        filterSelectOptions: filterQuestion,
        accessorFn: (row) => {
          const noticeOrders = row.NoticeOrders
          return (
            <div>
              {noticeOrders && noticeOrders.length > 0 ? (
                noticeOrders.map((order, index) => (
                  <div
                    key={order.id || index}
                    style={{
                      marginBottom: "8px",
                      borderBottom: "1px solid #eee",
                      paddingBottom: "4px",
                    }}
                  >
                    <div>
                      <strong>{getText("Déscription")} :</strong>{" "}
                      {order.description}
                    </div>
                    <div>
                      <Rating
                        name="read-only"
                        value={order.note || 0}
                        precision={0.1}
                        readOnly
                      />
                    </div>
                  </div>
                ))
              ) : (
                <span>{getText("Pas definie")}</span>
              )}
            </div>
          )
        },
      },
      {
        accessorFn: (row) => (
          <div style={{ width: "100%" }}>
            <ul>
              <li>
                {getText("Status de paiment")}:{" "}
                <span style={style}>
                  {row.orderStatus.haveAmountPaid
                    ? getText("Payé")
                    : getText("En attente")}
                </span>
              </li>
              <li>
                {getText("Quantité(s)")}:{" "}
                <span style={style}>{row.orderInvoice.quantity}</span>
              </li>
              <li>
                {getText("Montant artisan")}:{" "}
                <span style={style}>
                  {row.orderInvoice.craftsmanAmount.toFixed(0)}
                </span>
              </li>
              <li>
                {getText("Montant transport")}:{" "}
                <span style={style}>
                  {row.orderInvoice.shippingCost.toFixed(0)}
                </span>
              </li>
              <li>
                {getText("Montant commission")}:{" "}
                <span style={style}>
                  {row.orderInvoice.serviceCharge.toFixed(0)}
                </span>
              </li>
              <li>
                {getText("Montant Commande")}:{" "}
                <span style={style}>
                  {row.orderInvoice.orderFee.toFixed(0)}
                </span>
              </li>
              <li>
                {getText("Devise")}:{" "}
                <span style={style}>{row.orderInvoice.currency}</span>
              </li>
            </ul>
          </div>
        ),
        header: getText("Paiment"),
        size: 300,
        filterVariant: "select",
        filterSelectOptions: filterStatus,
        id: "Paiment",
      },
      {
        accessorFn: (row) => {
          if (!row?.DeliveryAdress) return getText("Pas definie")
          return `${getText("Pays")}: ${
            row.DeliveryAdress.Country?.name_fr || ""
          }
${getText("Ville")}: ${row.DeliveryAdress.cityName || ""}
${getText("Adresse")}: ${row.DeliveryAdress.address || ""}
${getText("Code postal")}: ${row.DeliveryAdress.zipCode || ""}`
        },
        Cell: ({ cell }) => {
          const [pays, ville, adresse, codePostal] = cell
            .getValue<string>()
            .split("\n")
          return (
            <div style={{ whiteSpace: "pre-line", ...style }}>
              {pays}
              <br />
              {ville}
              <br />
              {adresse}
              <br />
              {codePostal}
            </div>
          )
        },
        header: getText("Adresse de livraison"),
        enableClickToCopy: true,
        enableColumnFilter: false,
      },
      {
        accessorFn: (row) => (
          <span
            onClick={(e) => {
              e.stopPropagation()
              setOrderId(row.id)
              setOpenDetailOrder(true)
            }}
            style={{
              cursor: "pointer",
              color: "#1976d2",
              textDecoration: "underline",
            }}
          >
            {row.id || getText("Pas definie")}
          </span>
        ),
        header: "ID",
        enableEditing: false,
        size: 80,
        enableClickToCopy: true,
        id: "id",
      },
      {
        accessorFn: (row) =>
          row.Craftman?.country?.name_fr
            ? getByLang({
                value_fr: row.Craftman.country.name_fr,
                value_en: row.Craftman.country.name_en,
              })
            : getText("Pas definie"),
        header: getText("Pays de l'artisan"),
        enableClickToCopy: true,
        filterVariant: "select",
        filterSelectOptions: dataFilter?.countries.map(getValueFilter),
        id: "Pays de l'artisan",
      },
      {
        accessorFn: (row) =>
          row.createdAt
            ? moment(row.createdAt).format("DD MMM YYYY [à] HH[h]mm")
            : getText("Pas definie"),
        header: getText("Date de commande"),
        enableClickToCopy: true,
        filterVariant: "date",
        id: "Date de commande",
      },
      {
        accessorFn: (row) => getCustomerOrderPrice(row.Prices),
        header: getText("Prix du client"),
        enableClickToCopy: true,
        filterVariant: "text",
        id: "Prix du client",
      },
      {
        accessorFn: (row) => `${row.Customer?.country?.currency}`,
        header: getText("Devise"),
        enableClickToCopy: true,
        filterVariant: "text",
        id: "Devise",
      },
      {
        accessorFn: (row) =>
          `${row?.Categories?.flatMap((el) => el.name_fr)?.join("; ")}`,
        header: getText("Catégorie"),
        enableClickToCopy: true,
        filterVariant: "select",
        filterSelectOptions: dataFilter?.categories.map(getValueFilter),
        id: "Catégorie",
      },
      {
        accessorFn: (row) =>
          `${row?.ItemTypes?.flatMap((el) => el.name_fr)?.join("; ")}`,
        header: getText("Type article"),
        enableClickToCopy: true,
        filterVariant: "select",
        filterSelectOptions: dataFilter?.itemTypes.map(getValueFilter),
        id: "Type article",
      },
      {
        accessorFn: (row) =>
          `${row?.MaterialTypes?.flatMap((el) => el.name_fr)?.join("; ")}`,
        header: getText("Type matière"),
        enableClickToCopy: true,
        filterVariant: "select",
        filterSelectOptions: dataFilter?.materialTypes.map(getValueFilter),
        id: "Type matière",
      },
      {
        accessorFn: (row) => (
          <span
            onClick={(e) => {
              e.stopPropagation()
              setUserId(row?.Customer?.id || 0)
              setOpenDetailUser(true)
            }}
            style={{
              cursor: "pointer",
              color: "#1976d2",
              textDecoration: "underline",
            }}
          >
            {row.Customer?.id || getText("Pas definie")}
          </span>
        ),
        header: getText("ID du client"),
        filterVariant: "text",
        id: "ID du client",
        enableColumnFilter: !isOnlyCustomer,
      },
      {
        accessorFn: (row) => (
          <span
            onClick={(e) => {
              e.stopPropagation()
              setCraftmanId(row?.Craftman?.id || 0)
              setOpenDetailCraftman(true)
            }}
            style={{
              cursor: "pointer",
              color: "#1976d2",
              textDecoration: "underline",
            }}
          >
            {row?.Craftman?.id || getText("Pas definie")}
          </span>
        ),
        header: getText("ID de l'artisan"),
        filterVariant: "text",
        id: "ID de l'artisan",
        enableColumnFilter: !isOnlyCraftman,
      },
    ],
    [
      dataFilter,
      getText,
      filterStatus,
      isOnlyCraftman,
      isOnlyCustomer,
      filterQuestion,
      getByLang,
      getValueFilter,
      getCustomerOrderPrice,
    ],
  )

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleChangePagination = (pagination: Pagination) =>
    dispatch(setOrderPagination(pagination))

  const handleValidDelete = (id: number) => {
    dispatch(setLoadingDelete(true))
    deleteOrder({
      variables: { where: { id } },
      onCompleted: () => {
        dispatch(
          setOpenSnackbar({
            message: getText("La commande a été supprimée avec succès"),
            status: "success",
          }),
        )
        dispatch(setLoadingDelete(false))
        dispatch(setCloseAlert())
      },
      onError: (err) => {
        const message = getErrorsAsString(err)
        dispatch(setCloseAlert())
        dispatch(setOpenSnackbar({ message }))
      },
      update: (cache, { data }) => {
        updateCacheOrder({
          action: "delete",
          cache,
          entryData: { ...data?.deleteOneOrder, __typename: "Order" },
        })
      },
    })
  }

  const handleOpenDeleteDialog = (id: number) => {
    dispatch(
      setOpenAlert({
        handleValid: () => handleValidDelete(id),
        message: getText(
          "Êtes-vous vraiment sûr de vouloir supprimer cette commande ?",
        ),
        isLoading: false,
      }),
    )
  }

  const onChangeSearchValue = (value: string) => dispatch(setOrderQuery(value))

  const handleEdit = (id: number) => {
    dispatch(
      setOpenAlert({
        handleValid: () => {
          dispatch(setTitle(getText("Modifier une commande")))
          dispatch(setCloseAlert())
          navigate(`/order/edit/${id}`)
        },
        message: getText(
          "Êtes-vous vraiment sûr de vouloir modifier cette commande ?",
        ),
        isLoading: false,
      }),
    )
  }

  const handleView = (id: number) => {
    dispatch(setTitle(getText("Detail d'une commande")))
    navigate(`/order/detail/${id}`)
  }

  const handleRefresh = () => window.location.reload()

  const handleValidArchiveMany = () => {
    dispatch(setLoadingDelete(true))
    archiveMany({
      variables: {
        data: { status: { set: Status.Archive } },
        where: { id: { in: ids } },
      },
      onCompleted: () => {
        refetch()
        dispatch(
          setOpenSnackbar({
            message: getText("Les commandes ont été archivé avec succès"),
            status: "success",
          }),
        )
        dispatch(setLoadingDelete(false))
        setIds([])
        dispatch(setCloseAlert())
      },
      onError: (err) => {
        const message = getErrorsAsString(err)
        dispatch(setCloseAlert())
        dispatch(setOpenSnackbar({ message }))
      },
    })
  }

  const handleArchiveGroup = () => {
    dispatch(
      setOpenAlert({
        handleValid: () => handleValidArchiveMany(),
        message: getText(
          "Êtes-vous vraiment sûr de vouloir achiver ces commandes ?",
        ),
        isLoading: false,
      }),
    )
  }

  const onCloseDetailCraftman = useCallback(
    () => setOpenDetailCraftman(false),
    [],
  )
  const onCloseDetailUser = useCallback(() => setOpenDetailUser(false), [])
  const onCloseDetailOrder = useCallback(() => setOpenDetailOrder(false), [])

  // ── Render ────────────────────────────────────────────────────────────────
  if (loadingFilter) return <Loader />

  return (
    <>
      <CustomTable
        enableColumnFilters={true}
        columns={columns}
        data={data?.orders || []}
        lableAddNew={getText("Créer une nouvelle commande")}
        rootLisName={"/order/add"}
        isLoading={loading}
        showProgressBars={loading}
        rowCount={data?.aggregateOrder._count?._all || 0}
        handleChangePagination={handleChangePagination}
        pageIndex={page}
        pageSize={size}
        searchValue={query}
        selectedIds={ids}
        handleSelected={handleSelected}
        handleArchiveGroup={handleArchiveGroup}
        onChangeSearchValue={onChangeSearchValue}
        handleDelete={
          havePermissions(["order.delete"]) ? handleOpenDeleteDialog : undefined
        }
        isHaveHeaderAction={havePermissions(["order.create"])}
        handleEdit={havePermissions(["order.change"]) ? handleEdit : undefined}
        isHaveView={havePermissions(["order.view"])}
        handleView={havePermissions(["order.view"]) ? handleView : undefined}
        isHaveAction={havePermissions([
          "order.view",
          "order.change",
          "order.delete",
        ])}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        handleRefresh={handleRefresh}
        headerMobileFilter={
          <div
            style={{
              display: "flex",
              overflowX: "auto",
              whiteSpace: "nowrap",
              WebkitOverflowScrolling: "touch",
              marginBottom: "10px",
            }}
          >
            <ToggleButtonGroup
              color="info"
              value={filterMobile}
              exclusive
              onChange={handleChangeFilterMobile}
              aria-label="Filtre"
              style={{ display: "inline-flex" }}
            >
              <ToggleButton value="order_current">
                {getText("Date récente")}
              </ToggleButton>
              <ToggleButton value="order_paid">{getText("Payé")}</ToggleButton>
              <ToggleButton value="order_have_price">
                {getText("Proposition de prix")}
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
        }
      />
      <ModalDetailCraftman
        craftmanId={craftmanId}
        onClose={onCloseDetailCraftman}
        open={openDetailCraftman}
      />
      <ModalDetailUser
        customerId={userId}
        onClose={onCloseDetailUser}
        open={openDetailUser}
      />
      <ModalDetailOrder
        orderID={orderId}
        onClose={onCloseDetailOrder}
        open={openDetailOrder}
      />
    </>
  )
}

export default ListOrder
